export const siteData = {
  brand: {
    name: "P2Points",
    tagline: "Loyalty Growth Engine",
  },
  nav: [
    { label: "Features", href: "#features" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Industries", href: "#industries" },
    { label: "FAQ", href: "#faq" },
  ],
  hero: {
    headline: "Your Own Loyalty App.",
    headlineAccent: "Your Brand. Your Rules.",
    subheadline:
      "P2Points lets you launch your own branded loyalty app in days — reward customers, increase repeat visits, and grow your business with simple, offline-ready technology.",
    ctaPrimary: "Book a Demo",
    ctaSecondary: "See How It Works",
    image: "https://images.unsplash.com/photo-1660329496673-2caf5ba82117?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxsb3lhbHR5JTIwYXBwJTIwc21hcnRwaG9uZXxlbnwwfHx8fDE3NzE1NTk2MDB8MA&ixlib=rb-4.1.0&q=85",
  },
  problem: {
    headline: "Customers Love You.",
    headlineAccent: "But Do They Come Back?",
    points: [
      "Competing businesses are one click away",
      "Customers expect digital experiences",
      "Punch cards get lost or forgotten",
      "Your brand fades between visits",
      "Acquiring new customers costs 5x more than keeping existing ones",
    ],
    transition: "You don't need more ads. You need more repeat visits.",
  },
  solution: {
    headline: "Launch Your Own Loyalty App",
    headlineAccent: " — Without Building One.",
    items: [
      "Your own branded merchant app",
      "A customer rewards app",
      "Tap-to-earn rewards",
      "Tap-to-redeem perks",
      "Full control of your loyalty program",
    ],
    image: "https://images.unsplash.com/photo-1753161029695-f1d1e6881257?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMG93bmVyJTIwdGFibGV0fGVufDB8fHx8MTc3MTU1OTYwNXww&ixlib=rb-4.1.0&q=85",
  },
  features: [
    {
      icon: "Palette",
      title: "Your Brand Front and Center",
      description: "Your logo. Your colors. Your program. Customers see YOUR brand, not ours.",
    },
    {
      icon: "WifiOff",
      title: "Works Offline",
      description: 'No awkward "connection lost" moments. Rewards work everywhere, anytime.',
    },
    {
      icon: "Smartphone",
      title: "Simple to Use",
      description: "Tap to issue rewards. Tap to redeem. That's it. No training needed.",
    },
    {
      icon: "ShieldCheck",
      title: "No Payment System",
      description: "Just loyalty rewards. No financial complexity. No compliance headaches.",
    },
    {
      icon: "Lock",
      title: "Secure by Design",
      description: "Rewards can't be duplicated or faked. Every transaction is verified.",
    },
    {
      icon: "TrendingUp",
      title: "Growth Analytics",
      description: "See who's coming back, what's working, and how your loyalty program grows.",
    },
  ],
  howItWorks: {
    headline: "How It Works",
    headlineAccent: " — 3 Simple Steps",
    steps: [
      {
        number: "01",
        title: "Customer Earns",
        description: "Customer visits your business and taps to earn reward points instantly.",
      },
      {
        number: "02",
        title: "They Track Progress",
        description: "They see their rewards balance and progress toward perks in the app.",
      },
      {
        number: "03",
        title: "They Redeem & Return",
        description: "They redeem perks and keep coming back. The loyalty loop is complete.",
      },
    ],
  },
  imagine: {
    headline: "Imagine Your Customers Saying…",
    quotes: [
      {
        text: "I'll go back there — I'm close to a reward.",
        context: "Thinking about lunch",
      },
      {
        text: "I love their app.",
        context: "Showing a friend",
      },
      {
        text: "I get perks every time I visit.",
        context: "Telling their coworker",
      },
    ],
    image: "https://images.pexels.com/photos/4921155/pexels-photo-4921155.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  industries: {
    headline: "Built for Businesses",
    headlineAccent: " That Thrive on Repeat Visits",
    items: [
      {
        icon: "UtensilsCrossed",
        name: "Restaurants",
        benefit: "Turn first-time diners into weekly regulars",
      },
      {
        icon: "Coffee",
        name: "Cafés",
        benefit: "Reward every cup and build daily habits",
      },
      {
        icon: "Dumbbell",
        name: "Gyms",
        benefit: "Boost member retention and class attendance",
      },
      {
        icon: "Scissors",
        name: "Salons",
        benefit: "Keep clients coming back on schedule",
      },
      {
        icon: "Heart",
        name: "Clinics",
        benefit: "Encourage regular check-ups and referrals",
      },
      {
        icon: "ShoppingBag",
        name: "Retail Shops",
        benefit: "Drive repeat purchases and bigger baskets",
      },
    ],
  },
  whiteLabel: {
    headline: "This Isn't Our App.",
    headlineAccent: " It's Yours.",
    points: [
      "Your own merchant app",
      "Your loyalty rules",
      "Your reward types",
      "Your branding",
    ],
    description:
      "Customers use the P2Points Rewards App to collect perks, but your business controls the experience.",
    image: "https://images.pexels.com/photos/8306371/pexels-photo-8306371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  faq: [
    {
      question: "Is this a payment app?",
      answer:
        "No. P2Points is a loyalty platform. No money is stored, transferred, or processed. It's purely about rewarding your customers for their visits.",
    },
    {
      question: "Can customers redeem rewards at other stores?",
      answer:
        "No. Your rewards stay within your business. You control what you offer and how customers earn and redeem.",
    },
    {
      question: "Does it work without internet?",
      answer:
        "Yes. P2Points is built with offline-first technology, so your loyalty program works even when connectivity is spotty.",
    },
    {
      question: "Can I customize my loyalty program?",
      answer:
        "Absolutely. Set your own rules, reward thresholds, perk types, and branding. It's your program, your way.",
    },
    {
      question: "How long does it take to launch?",
      answer:
        "Days, not months. We'll get you set up with your branded app and loyalty program quickly so you can start growing.",
    },
  ],
  finalCta: {
    headline: "Ready to Turn Visits",
    headlineAccent: " Into Loyalty?",
    ctaPrimary: "Book a Demo",
    ctaSecondary: "Launch My Loyalty App",
  },
  footer: {
    disclaimer:
      "P2Points provides loyalty and promotional rewards software. Rewards are defined by each merchant's program rules. P2Points does not store customer funds and does not provide payment or cash-out services.",
    links: [
      { label: "Privacy Policy", href: "#" },
      { label: "Terms of Service", href: "#" },
      { label: "Contact", href: "#" },
    ],
  },
};

// Demo form submissions storage (mock)
export const demoRequests = [];

export const addDemoRequest = (data) => {
  const request = {
    id: Date.now().toString(),
    ...data,
    timestamp: new Date().toISOString(),
  };
  demoRequests.push(request);
  return request;
};
